import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { DelegateRole, DelegateStatus, LoginResultDto } from '../data-models/identity.data.models';
import { InvestingType } from '../data-models/kyc.data.models';
import { SignalrService } from 'src/app/shared/services/signalr.service';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UserService implements OnDestroy {
  private _currentUserCredantials?: LoginResultDto;
  private readonly storageListener = (event: StorageEvent): void => {
    if (event.key !== 'currentUser') {
      return;
    }

    const credentials = event.newValue
      ? JSON.parse(event.newValue) as LoginResultDto
      : undefined;
    this._currentUserCredantials = credentials;
    this.userCredentialsSubject.next(credentials);
  };
  private userCredentialsSubject: BehaviorSubject<LoginResultDto | undefined> = new BehaviorSubject<LoginResultDto | undefined>(
    localStorage.getItem("currentUser") ? JSON.parse(localStorage.getItem("currentUser") as string) : undefined
  );
  public currentUserCredantials$: Observable<LoginResultDto | undefined> = this.userCredentialsSubject.asObservable();

  constructor(
    private router: Router,
    private signalrService: SignalrService
  ) {
    window.addEventListener('storage', this.storageListener);
  }

  ngOnDestroy(): void {
    window.removeEventListener('storage', this.storageListener);
  }

  getCurrentSystemLanguage(): string {
    let language = localStorage.getItem('language');
    if (language) return language;
    else {
      localStorage.setItem('language', 'en-US');
      return 'en-US';
    }
  }
  setLanguage(language: string) {
    localStorage.setItem('language', language);
  }

  get currentUserCredantials(): LoginResultDto {
    if (localStorage.getItem("currentUser")) {
      return JSON.parse(localStorage.getItem("currentUser") as string) as LoginResultDto;
    }
    else
      return undefined!;

  }
  set currentUserCredantials(value: LoginResultDto | undefined) {
    if (value) {
      if (value.expiryTokenInMinutes) {
        var expiryDate = new Date();
        expiryDate.setMinutes(expiryDate.getMinutes() + value.expiryTokenInMinutes! - 5);
        value.expiryTokenDate = expiryDate;
      }
      const rawRole = value.delegateRole ?? value.DelegateRole;
      const rawStatus = value.delegateStatus ?? value.DelegateStatus;
      if (rawRole != null) {
        value.delegateRole = this.parseDelegateRole(rawRole);
        value.DelegateRole = value.delegateRole;
      }
      if (rawStatus != null) {
        value.delegateStatus = this.parseDelegateStatus(rawStatus);
        value.DelegateStatus = value.delegateStatus;
      }
    }
    this._currentUserCredantials = value == undefined ? value : JSON.parse(JSON.stringify(value));
    localStorage.setItem("currentUser", this._currentUserCredantials ? JSON.stringify(this._currentUserCredantials) : "");
    this.userCredentialsSubject.next(this._currentUserCredantials);

  }

  private parseDelegateRole(role: any): DelegateRole | undefined {
    if (role == null || role === '') return undefined;
    if (typeof role === 'number') {
      if (role === DelegateRole.Primary || role === DelegateRole.ReadOnly) {
        return role;
      }
    }
    const str = String(role).trim().toLowerCase();
    if (str === '1' || str === 'primary') {
      return DelegateRole.Primary;
    }
    if (str === '2' || str === 'readonly' || str === 'read_only') {
      return DelegateRole.ReadOnly;
    }
    return undefined;
  }

  private parseDelegateStatus(status: any): DelegateStatus | undefined {
    if (status == null || status === '') return undefined;
    if (typeof status === 'number') {
      if (status === DelegateStatus.Pending || status === DelegateStatus.Approved || status === DelegateStatus.Rejected) {
        return status;
      }
    }
    const str = String(status).trim().toLowerCase();
    if (str === '1' || str === 'pending') {
      return DelegateStatus.Pending;
    }
    if (str === '2' || str === 'approved') {
      return DelegateStatus.Approved;
    }
    if (str === '3' || str === 'rejected') {
      return DelegateStatus.Rejected;
    }
    return undefined;
  }

  get delegateRole(): DelegateRole | undefined {
    const creds = this.currentUserCredantials;
    return creds ? this.parseDelegateRole(creds.delegateRole ?? creds.DelegateRole) : undefined;
  }

  get delegateStatus(): DelegateStatus | undefined {
    const creds = this.currentUserCredantials;
    return creds ? this.parseDelegateStatus(creds.delegateStatus ?? creds.DelegateStatus) : undefined;
  }

  get isDelegate(): boolean {
    const creds = this.currentUserCredantials;
    if (!creds) return false;
    const rawRole = creds.delegateRole ?? creds.DelegateRole;
    const rawStatus = creds.delegateStatus ?? creds.DelegateStatus;
    return (rawRole != null && rawRole !== '') || (rawStatus != null && rawStatus !== '');
  }

  get isPendingDelegate(): boolean {
    return this.isDelegate && this.delegateStatus === DelegateStatus.Pending;
  }

  get isApprovedDelegate(): boolean {
    return this.isDelegate && this.delegateStatus === DelegateStatus.Approved;
  }

  get isRejectedDelegate(): boolean {
    return this.isDelegate && this.delegateStatus === DelegateStatus.Rejected;
  }

  get isPrimaryDelegate(): boolean {
    return this.isDelegate && this.delegateRole === DelegateRole.Primary;
  }

  get isReadOnlyDelegate(): boolean {
    return this.isDelegate && this.delegateRole === DelegateRole.ReadOnly;
  }

  get canPerformCompanyActions(): boolean {
    if (!this.isDelegate) {
      return true;
    }
    if (this.isPendingDelegate) {
      return false;
    }
    if (this.isReadOnlyDelegate) {
      return false;
    }
    if (this.isPrimaryDelegate && this.isApprovedDelegate) {
      return true;
    }
    return false;
  }

  get isCompany(): boolean {
    const creds = this.currentUserCredantials;
    if (!creds) return false;
    const investType: any = creds.investTypeId;
    return investType == InvestingType.Company || String(investType).trim().toLowerCase() === 'company';
  }

  get canUpgradeProfile(): boolean {
    if (this.isCompany) {
      return false;
    }
    if (
      this.isDelegate ||
      this.isPrimaryDelegate ||
      this.isReadOnlyDelegate ||
      this.delegateRole === DelegateRole.Primary ||
      this.delegateRole === DelegateRole.ReadOnly
    ) {
      return false;
    }
    return true;
  }

  updateUserKYCStatus(status: string) {
    const creds = this.currentUserCredantials;
    if (creds && creds.userKYCStatus !== status) {
      this.currentUserCredantials = {
        ...creds,
        userKYCStatus: status
      };
    }
  }

  isLoggedIn(): boolean {
    if (this.currentUserCredantials)
      return true;
    else
      return false;
  }

  logout() {
    this.clearLocalStorage();
    this.signalrService.stopConnection();
    this.router.navigate(['/authentication/login']).then(() => {
      window.location.reload();
    });
  }
  clearLocalStorage() {
    this._currentUserCredantials = undefined;
    localStorage.removeItem('currentUser');
    localStorage.removeItem('investTypeId');
    localStorage.removeItem('requestId');
    this.userCredentialsSubject.next(undefined);
  }

}